import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Save, Loader2, Download, Plus, Trash2,
  Check, AlertCircle, FileText, Mail, Briefcase, ExternalLink, RotateCcw,
  Dot, Sparkles
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { humanizeApiError, toSafeDisplayString } from '../lib/apiErrors';
import { ReviewBeforeDownloadModal } from '../components/ReviewBeforeDownloadModal';
import {
  fetchEditorData,
  fetchSingleJobResult,
  saveCvEdits,
  saveCvSuggestionDecisions,
  saveCoverLetterEdits,
  downloadTailoredCvPdf,
  downloadTailoredCvDocx,
  fetchTailoredCvStatus,
} from '../data/mockJobResults';
import { Check as CheckIcon, X as XIcon, Edit2 as Edit2Icon, AlertTriangle as AlertTriangleIcon } from 'lucide-react';
import { Textarea } from '../components/ui/textarea';

// ── Helpers to normalize the many CV JSON shapes into a stable editor model

function toArray(v) {
  if (v == null) return [];
  return Array.isArray(v) ? v : [v];
}

function cleanText(value) {
  return String(value == null ? '' : value).replace(/\s+/g, ' ').trim();
}

function cvDateDisplay(entry = {}) {
  const direct = cleanText(
    entry.date_range_display ||
    entry.date ||
    entry.dateRange ||
    entry.dates ||
    entry.year
  );
  if (direct) return direct;
  const start = cleanText(entry.start_date || entry.startYear);
  const end = cleanText(entry.end_date || entry.endYear);
  if (start && end) return `${start} - ${end}`;
  return start || end;
}

function cvLanguageLevel(entry = {}) {
  return cleanText(entry.proficiency || entry.level || entry.fluency);
}

const NON_INSERTABLE_SUGGESTION_PHRASES = [
  'add a bullet about',
  'add a bullet showing',
  'add a bullet mentioning',
  'add or strengthen',
  'add relevant',
  'mention exposure to',
  'mention involvement in',
  'include exposure to',
  'include a bullet about',
  'highlight exposure to',
];

// Returns true only when a CV JSON object has at least one real value
// across the meaningful fields. Treats {} / null / undefined / object with
// only empty arrays / blank strings as "no meaningful content".
function hasMeaningfulCvContent(cv) {
  if (!cv || typeof cv !== 'object' || Array.isArray(cv)) return false;
  const header = cv.header || {};
  const headerName = (
    header.name || header.full_name || cv.name || cv.full_name || ''
  ).toString().trim();
  if (headerName) return true;

  const summary = (cv.objective || cv.professional_summary || cv.summary || '').toString().trim();
  if (summary) return true;

  const skills = cv.skills;
  if (Array.isArray(skills) && skills.length > 0) return true;
  if (skills && typeof skills === 'object' && !Array.isArray(skills) && Object.keys(skills).length > 0) return true;

  const arrayFields = ['experience', 'work_experience', 'education', 'projects', 'languages', 'certifications'];
  for (const k of arrayFields) {
    const v = cv[k];
    if (Array.isArray(v) && v.length > 0) return true;
  }
  return false;
}

/**
 * Normalize any incoming CV JSON into a predictable editable structure.
 * We keep unknown top-level keys untouched (returned as `_extra`) so we never lose data on save.
 */
function normalizeCv(cv) {
  if (!cv || typeof cv !== 'object') cv = {};
  const header = cv.header || {};

  // Summary: prefer objective, fall back to professional_summary
  const summary = cv.objective || cv.professional_summary || '';
  const summaryKey = cv.objective !== undefined ? 'objective' : 'professional_summary';

  // Skills: normalize to [{category, items:[]}]
  let skillGroups = [];
  const rawSkills = cv.skills;
  if (Array.isArray(rawSkills)) {
    const hasGrouped = rawSkills.some((s) => s && typeof s === 'object' && (s.category || s.items));
    if (hasGrouped) {
      skillGroups = rawSkills.map((g) => ({
        category: g.category || 'Skills',
        items: toArray(g.items).map((i) => (typeof i === 'string' ? i : i.name || i.skill || '')),
      }));
    } else {
      skillGroups = [{
        category: 'Skills',
        items: rawSkills.map((s) => (typeof s === 'string' ? s : s.name || s.skill || '')),
      }];
    }
  } else if (rawSkills && typeof rawSkills === 'object') {
    skillGroups = Object.entries(rawSkills).map(([cat, items]) => ({
      category: cat,
      items: toArray(items).map((i) => (typeof i === 'string' ? i : i.name || i.skill || '')),
    }));
  }

  // Experience
  const experience = toArray(cv.experience).map((e) => ({
    title: e.title || e.role || '',
    company: e.company || '',
    location: e.location || '',
    dates: cvDateDisplay(e),
    date_range_display: e.date_range_display || '',
    dateRange: e.dateRange || '',
    start_date: e.start_date || '',
    end_date: e.end_date || '',
    startYear: e.startYear || '',
    endYear: e.endYear || '',
    bullets: toArray(e.bullets),
    description: e.description || '',
  }));

  // Projects
  const projects = toArray(cv.projects).map((p) => ({
    name: p.name || p.title || '',
    description: p.description || '',
    bullets: toArray(p.bullets),
  }));

  // Education
  const education = toArray(cv.education).map((e) => {
    if (typeof e === 'string') return { degree: e, institution: '', location: '', year: '' };
    return {
      degree: e.degree || e.title || '',
      institution: e.institution || '',
      location: e.location || '',
      year: cvDateDisplay(e),
      date_range_display: e.date_range_display || '',
      dateRange: e.dateRange || '',
      dates: e.dates || '',
      start_date: e.start_date || '',
      end_date: e.end_date || '',
      startYear: e.startYear || '',
      endYear: e.endYear || '',
    };
  });

  // Languages: array of {language, level}
  let languages = [];
  const rawLangs = cv.languages;
  if (Array.isArray(rawLangs)) {
    languages = rawLangs.map((l) => {
      if (typeof l === 'string') return { language: l, level: '' };
      return { language: l.language || l.name || '', level: cvLanguageLevel(l), proficiency: l.proficiency || '', fluency: l.fluency || '' };
    });
  } else if (rawLangs && typeof rawLangs === 'object') {
    languages = Object.entries(rawLangs).map(([language, level]) => ({
      language,
      level: typeof level === 'string' ? level : '',
    }));
  }

  // Certifications (optional simple text list)
  const certifications = toArray(cv.certifications).map((c) => {
    if (typeof c === 'string') return c;
    return c.name || c.title || '';
  });

  // Preserve any other top-level fields so we round-trip cleanly
  const KNOWN = new Set([
    'header', 'objective', 'professional_summary', 'skills',
    'experience', 'projects', 'education', 'languages', 'certifications', 'name', 'email', 'phone', 'location',
  ]);
  const _extra = {};
  Object.keys(cv).forEach((k) => { if (!KNOWN.has(k)) _extra[k] = cv[k]; });

  return {
    header: {
      name: header.name || cv.name || '',
      email: header.email || cv.email || '',
      phone: header.phone || cv.phone || '',
      location: header.location || cv.location || '',
      linkedin: header.linkedin || '',
      github: header.github || '',
    },
    summary,
    summaryKey,
    skillGroups,
    experience,
    projects,
    education,
    languages,
    certifications,
    _extra,
  };
}

/** Convert editor model back to CV JSON shape used by backend/exports */
function denormalizeCv(m) {
  const out = { ...(m._extra || {}) };
  out.header = { ...m.header };
  if (m.summary) out[m.summaryKey || 'objective'] = m.summary;

  // Skills: if only 1 group named "Skills" just flatten; else keep grouped shape
  if (m.skillGroups.length === 1 && m.skillGroups[0].category === 'Skills') {
    out.skills = m.skillGroups[0].items.filter(Boolean);
  } else {
    out.skills = m.skillGroups
      .map((g) => ({ category: g.category, items: g.items.filter(Boolean) }))
      .filter((g) => g.items.length > 0 || g.category);
  }

  out.experience = m.experience.map((e) => {
    const row = { title: e.title, company: e.company };
    if (e.location) row.location = e.location;
    if (e.dates) {
      row.dates = e.dates;
      row.date_range_display = e.date_range_display || e.dates;
    }
    if (e.dateRange) row.dateRange = e.dateRange;
    if (e.start_date) row.start_date = e.start_date;
    if (e.end_date) row.end_date = e.end_date;
    if (e.startYear) row.startYear = e.startYear;
    if (e.endYear) row.endYear = e.endYear;
    const bullets = e.bullets.filter(Boolean);
    if (bullets.length) row.bullets = bullets;
    if (e.description) row.description = e.description;
    return row;
  });

  out.projects = m.projects.map((p) => {
    const row = { name: p.name };
    if (p.description) row.description = p.description;
    const bullets = p.bullets.filter(Boolean);
    if (bullets.length) row.bullets = bullets;
    return row;
  });

  out.education = m.education.map((e) => {
    const row = { degree: e.degree };
    if (e.institution) row.institution = e.institution;
    if (e.location) row.location = e.location;
    if (e.year) {
      row.year = e.year;
      row.date_range_display = e.date_range_display || e.year;
    }
    if (e.dates) row.dates = e.dates;
    if (e.dateRange) row.dateRange = e.dateRange;
    if (e.start_date) row.start_date = e.start_date;
    if (e.end_date) row.end_date = e.end_date;
    if (e.startYear) row.startYear = e.startYear;
    if (e.endYear) row.endYear = e.endYear;
    return row;
  });

  out.languages = m.languages
    .filter((l) => l.language)
    .map((l) => ({ language: l.language, level: l.level, proficiency: l.proficiency || l.level, fluency: l.fluency || '' }));

  out.certifications = m.certifications.filter(Boolean);

  return out;
}

// ── AI-tailored change indicators ─────────────────────────────────────
//
// Source: backend's `cv_changes_summary` (free-text summary written by the AI
// during CV tailoring) + `cv_unsupported_items_avoided` (items the AI chose
// NOT to fabricate).
//
// We do NOT diff the user's edits against anything. We only surface what the
// AI reported it changed when it tailored the CV from the user's original
// upload. Because cv_changes_summary is free-text, we cannot identify the
// exact bullet that was changed — only which top-level sections were touched,
// via simple keyword matching. This is heuristic and intentionally
// conservative; if no keyword for a section matches, no badge is shown.

const SECTION_KEYWORDS = {
  summary:    /\b(summary|objective|profile|professional summary)\b/i,
  skills:     /\bskill/i,
  experience: /\b(experience|role|work history|bullet|achievement|impact|responsibilit)/i,
  projects:   /\bproject/i,
  education:  /\b(education|degree|coursework|university|school)/i,
  languages:  /\blanguage/i,
};

function detectAiChangedSections(cvChangesSummary) {
  const text = (cvChangesSummary || '').trim();
  if (!text) return null;
  const out = {};
  let any = false;
  for (const [key, re] of Object.entries(SECTION_KEYWORDS)) {
    out[key] = re.test(text);
    if (out[key]) any = true;
  }
  return any ? out : null;
}

function AiTailoredBadge({ testid }) {
  return (
    <span
      className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200/80 dark:bg-amber-950/30 dark:text-amber-300 dark:border-amber-800/60"
      data-testid={testid}
      title="The AI tailored content in this section to match the role."
    >
      <Sparkles className="w-3 h-3" /> AI tailored
    </span>
  );
}

// ── Tiny reusable editors ─────────────────────────────────────────────

const fieldCls = 'w-full text-sm bg-background border border-border rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-foreground/20';

function SectionHeader({ icon: Icon, title, badge, children }) {
  return (
    <div className="flex items-center justify-between mb-3">
      <div className="flex items-center gap-2">
        {Icon && <Icon className="w-4 h-4 text-muted-foreground" />}
        <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">{title}</h3>
        {badge}
      </div>
      {children}
    </div>
  );
}

function BulletList({ bullets, onChange, testidPrefix }) {
  const update = (i, v) => { const n = [...bullets]; n[i] = v; onChange(n); };
  const add = () => onChange([...bullets, '']);
  const remove = (i) => onChange(bullets.filter((_, idx) => idx !== i));
  return (
    <div className="space-y-1.5">
      {bullets.map((b, i) => (
        <div key={i} className="flex items-start gap-2" data-testid={`${testidPrefix}-bullet-${i}`}>
          <span className="text-muted-foreground text-sm mt-2">-</span>
          <textarea
            value={b}
            onChange={(e) => update(i, e.target.value)}
            rows={2}
            className={fieldCls + ' resize-y min-h-[40px]'}
            data-testid={`${testidPrefix}-bullet-input-${i}`}
          />
          <button onClick={() => remove(i)} className="text-muted-foreground hover:text-red-500 mt-1.5" aria-label="Remove bullet" data-testid={`${testidPrefix}-bullet-remove-${i}`}>
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      ))}
      <button onClick={add} className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground" data-testid={`${testidPrefix}-bullet-add`}>
        <Plus className="w-3 h-3" /> Add bullet
      </button>
    </div>
  );
}

/**
 * Inline save-status badge.
 * Shows one of: Saving… / Unsaved changes / Saved / Save failed
 */
function SaveStatusBadge({ saving, dirty, status, errorMsg, savedAt, testid }) {
  if (saving) {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-muted-foreground" data-testid={`${testid}-saving`}>
        <Loader2 className="w-3 h-3 animate-spin" /> Saving…
      </span>
    );
  }
  if (status === 'error') {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-red-600 dark:text-red-400" title={toSafeDisplayString(errorMsg, { context: 'editor-save' })} data-testid={`${testid}-error`}>
        <AlertCircle className="w-3 h-3" /> Could not save. Try again.
      </span>
    );
  }
  if (dirty) {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-amber-600 dark:text-amber-400" data-testid={`${testid}-dirty`}>
        <Dot className="w-3 h-3" /> Unsaved changes
      </span>
    );
  }
  if (status === 'saved') {
    const label = savedAt ? 'Saved just now' : 'Saved';
    return (
      <span className="inline-flex items-center gap-1 text-xs text-emerald-600 dark:text-emerald-400" data-testid={`${testid}-saved`}>
        <Check className="w-3 h-3" /> {label}
      </span>
    );
  }
  if (status === 'no-changes') {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-muted-foreground" data-testid={`${testid}-no-changes`}>
        <Dot className="w-3 h-3" /> No changes to save.
      </span>
    );
  }
  return null;
}

// ── CV Suggestion Decisions (Approve / Edit / Reject) ─────────────────

// Stable key for a suggestion so we can correlate across reloads even though
// the AI doesn't emit a unique id.
function suggestionKey(s, idx) {
  const sect = String(s?.section || '').trim().toLowerCase();
  const text = String(s?.suggestedCvLine || s?.whatToAdd || '').trim().slice(0, 220);
  // Idx is a tie-breaker so two near-duplicate suggestions still get separate keys.
  return `${sect}|${text}|${idx}`;
}

// A suggestion needs the 3 truth-confirmation checkboxes whenever it is not
// confidently CV-supported. We treat anything labelled unsupported / unknown
// or carrying a warning as "needs truth confirmation". Approved-as-edited
// always allows the user to confirm too.
function isUnsupportedSuggestion(s) {
  if (!safeInsertableText(s)) return false;
  const sl = String(s?.supportLevel || '').toLowerCase();
  if (!sl) return !!s?.warning;
  return sl.includes('unsupported') || sl.includes('unknown') || sl.includes('weak') || !!s?.warning;
}

const UNSAFE_SUGGESTION_PHRASES = [
  'do not add unless true', 'do not add unless', 'only add if true', 'only add this if true',
  'only if true', 'if true', 'if accurate', 'if applicable', 'if you have experience',
  'unless supported', 'verify first', 'verify before adding', 'would need to develop',
  'missing', 'lacks', 'no direct experience', 'does not have',
  'not yet present', 'not yet supported',
];

const CV_SUGGESTION_DISPLAY_ONLY_FIELDS = [
  'warning', 'caution', 'evidence_note', 'evidence', 'reason',
  'whyItMatters', 'why_it_matters', 'title', 'label', 'display_text',
];

function safeInsertableText(s) {
  if (s?.is_addable === false) return '';
  const supportLevel = String(s?.supportLevel || s?.support_level || s?.support || s?.evidence_status || '').toLowerCase();
  if (supportLevel.includes('unsupported') || supportLevel.includes('unknown') || supportLevel.includes('not_source_backed')) return '';
  if (CV_SUGGESTION_DISPLAY_ONLY_FIELDS.some((field) => containsUnsafeSuggestionPhrase(s?.[field]))) return '';
  const text = String(s?.insertable_text || '').trim();
  if (!text) return '';
  const lower = text.toLowerCase();
  if (UNSAFE_SUGGESTION_PHRASES.some((phrase) => lower.includes(phrase))) return '';
  if (NON_INSERTABLE_SUGGESTION_PHRASES.some((phrase) => lower.includes(phrase))) return '';
  return text;
}

function containsUnsafeSuggestionPhrase(value) {
  const lower = String(value || '').toLowerCase();
  return UNSAFE_SUGGESTION_PHRASES.some((phrase) => lower.includes(phrase));
}

function isSafeSuggestionEditText(value) {
  const lower = String(value || '').trim().toLowerCase();
  return Boolean(lower)
    && !UNSAFE_SUGGESTION_PHRASES.some((phrase) => lower.includes(phrase))
    && !NON_INSERTABLE_SUGGESTION_PHRASES.some((phrase) => lower.includes(phrase));
}

function suggestionLooksLikeUnsupportedGap(s) {
  const supportLevel = String(s?.supportLevel || s?.support_level || s?.support || s?.evidence_status || '').toLowerCase();
  if (supportLevel.includes('unsupported') || supportLevel.includes('unknown') || supportLevel.includes('not_source_backed')) return true;
  return CV_SUGGESTION_DISPLAY_ONLY_FIELDS.some((field) => containsUnsafeSuggestionPhrase(s?.[field]));
}

function normalizeSuggestionForEditor(raw, idx) {
  const source = raw && typeof raw === 'object' ? raw : { display_text: raw };
  const insertable = safeInsertableText(source);
  const displayText = String(
    source.display_text || source.whatToAdd || source.what_to_add || source.plainEnglishAction ||
    source.suggestion || source.reason || source.whyItMatters || source.why_it_matters ||
    source.suggestedCvLine || source.insertable_text || ''
  ).trim();
  const recommendationOnly = NON_INSERTABLE_SUGGESTION_PHRASES.some((phrase) => displayText.toLowerCase().includes(phrase));
  const blockedUnsupported = suggestionLooksLikeUnsupportedGap(source);
  const isAddable = Boolean(insertable) && !recommendationOnly && !blockedUnsupported && source.is_addable !== false;
  return {
    ...source,
    id: source.id || `${idx}-${(insertable || displayText || 'suggestion').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 80)}`,
    insertable_text: isAddable ? insertable : '',
    display_text: containsUnsafeSuggestionPhrase(displayText) ? '' : displayText,
    is_addable: isAddable,
    supportLevel: source.supportLevel || source.support_level || (blockedUnsupported ? 'unsupported' : (isAddable ? 'supported' : 'diagnostic')),
    warning: containsUnsafeSuggestionPhrase(source.warning) ? '' : (source.warning || (!isAddable && displayText ? 'This is diagnostic only. Edit it into a specific CV-ready bullet before adding.' : '')),
  };
}

const TRUTH_CHECKS = [
  { key: 'cv_evidence', label: 'I have the experience or evidence in my CV to back this up.' },
  { key: 'honest', label: 'I am not adding skills, tools, or achievements I cannot demonstrate.' },
  { key: 'aware', label: 'I understand recruiters may ask me about this during interviews.' },
];

function CvSuggestionsPanel({ suggestions, decisions, onDecisionsChange, onApply, disabled }) {
  // decisions: { [suggestion_key]: { status, edited_text, applied_section, truth_acknowledged, decided_at } }
  // onApply(suggestionKey, sourceText, section) → caller inserts into cvModel
  const [editingKey, setEditingKey] = useState(null);
  const [draftText, setDraftText] = useState('');
  const [truthState, setTruthState] = useState({}); // local per-key truth checks before commit

  if (!Array.isArray(suggestions) || suggestions.length === 0) return null;

  const setDecision = (key, patch) => {
    const prev = decisions[key] || { status: 'pending', edited_text: null, applied_section: null, truth_acknowledged: false };
    const next = { ...prev, ...patch, decided_at: new Date().toISOString() };
    onDecisionsChange({ ...decisions, [key]: next });
  };

  const truthAcknowledgedFor = (key) => {
    const d = decisions[key];
    if (d?.truth_acknowledged) return true;
    const t = truthState[key] || {};
    return TRUTH_CHECKS.every((c) => !!t[c.key]);
  };

  const toggleTruth = (key, field) => {
    setTruthState((s) => ({ ...s, [key]: { ...(s[key] || {}), [field]: !s[key]?.[field] } }));
  };

  const handleApprove = (key, s, idx) => {
    const needsTruth = isUnsupportedSuggestion(s);
    if (needsTruth && !truthAcknowledgedFor(key)) return; // gate
    const text = safeInsertableText(s);
    if (!text.trim()) return;
    onApply(key, text, s.section || s.target_section);
    setDecision(key, {
      status: 'approved',
      edited_text: null,
      applied_section: s.section || s.target_section || null,
      truth_acknowledged: !!needsTruth,
    });
  };

  const handleStartEdit = (key, s) => {
    setEditingKey(key);
    setDraftText(decisions[key]?.edited_text || safeInsertableText(s) || '');
  };

  const handleSaveEdit = (key, s) => {
    const needsTruth = isUnsupportedSuggestion(s);
    if (needsTruth && !truthAcknowledgedFor(key)) return;
    const text = (draftText || '').trim();
    if (!text) return;
    if (!isSafeSuggestionEditText(text)) return;
    onApply(key, text, s.section || s.target_section);
    setDecision(key, {
      status: 'edited',
      edited_text: text,
      applied_section: s.section || s.target_section || null,
      truth_acknowledged: !!needsTruth,
    });
    setEditingKey(null);
    setDraftText('');
  };

  const handleReject = (key) => {
    // Reject NEVER calls /cv-edits. It only updates the decisions ledger.
    setDecision(key, { status: 'rejected', edited_text: null, applied_section: null, truth_acknowledged: false });
    if (editingKey === key) setEditingKey(null);
  };

  return (
    <div
      className="border border-border rounded-lg overflow-hidden mb-6"
      data-testid="cv-suggestions-panel"
    >
      <div className="p-4 bg-surface/40 border-b border-border flex items-center gap-2">
        <Sparkles className="w-4 h-4 text-amber-500" />
        <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
          AI CV Suggestions
        </h3>
        <span className="text-[11px] text-muted-foreground ml-auto">
          {suggestions.length} suggestion{suggestions.length === 1 ? '' : 's'}
        </span>
      </div>

      <ul className="divide-y divide-border">
        {suggestions.map((s, idx) => {
          const key = suggestionKey(s, idx);
          const decision = decisions[key];
          const status = decision?.status || 'pending';
          const isApproved = status === 'approved' || status === 'edited';
          const isRejected = status === 'rejected';
          const isEditing = editingKey === key;
              const needsTruth = isUnsupportedSuggestion(s);
              const insertableText = safeInsertableText(s);
          const displayOnlyText = String(s.display_text || s.whatToAdd || s.what_to_add || s.plainEnglishAction || s.suggestion || '').trim();
          const safeWhatToAdd = containsUnsafeSuggestionPhrase(displayOnlyText) ? '' : displayOnlyText;
          const safeSuggestedLine = insertableText;
          const safeWhyItMatters = containsUnsafeSuggestionPhrase(s.whyItMatters) ? '' : String(s.whyItMatters || '').trim();
          const safeWarning = containsUnsafeSuggestionPhrase(s.warning) ? '' : String(s.warning || '').trim();
          const truthOk = truthAcknowledgedFor(key);

          return (
            <li key={key} className="p-4 space-y-3" data-testid={`cv-suggestion-row-${idx}`}>
              {/* Header */}
              <div className="flex items-start gap-3">
                {(s.section || s.target_section) && (
                  <span className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground bg-border/40 px-2 py-0.5 rounded shrink-0">
                    {s.section || s.target_section}
                  </span>
                )}
                <div className="flex-1 min-w-0">
                  {safeWhatToAdd && <p className="text-sm text-foreground">{safeWhatToAdd}</p>}
                  {safeSuggestedLine && (
                    <div className="mt-2 p-3 border border-emerald-200 dark:border-emerald-800 rounded-md bg-emerald-50/30 dark:bg-emerald-950/20">
                      <p className="text-sm leading-relaxed text-foreground italic">&ldquo;{safeSuggestedLine}&rdquo;</p>
                    </div>
                  )}
                  {safeWhyItMatters && (
                    <p className="text-xs text-muted-foreground leading-relaxed mt-2">{safeWhyItMatters}</p>
                  )}
                  {s.supportLevel && (
                    <p className="text-xs text-muted-foreground mt-1">
                      <span className="font-medium">Support:</span> {s.supportLevel}
                    </p>
                  )}
                  {safeWarning && (
                    <div className="mt-2 flex items-start gap-1.5 text-xs text-amber-700 dark:text-amber-400">
                      <AlertTriangleIcon className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                      <span>{safeWarning}</span>
                    </div>
                  )}
                </div>
                {isApproved && (
                  <span
                    className="inline-flex items-center gap-1 text-[11px] text-emerald-700 dark:text-emerald-300 shrink-0"
                    data-testid={`cv-suggestion-status-approved-${idx}`}
                  >
                    <CheckIcon className="w-3 h-3" /> {status === 'edited' ? 'Edited & approved' : 'Approved'}
                  </span>
                )}
                {isRejected && (
                  <span
                    className="inline-flex items-center gap-1 text-[11px] text-muted-foreground shrink-0"
                    data-testid={`cv-suggestion-status-rejected-${idx}`}
                  >
                    <XIcon className="w-3 h-3" /> Rejected
                  </span>
                )}
              </div>

              {/* Truth confirmation (unsupported / unknown only, before approve / edit-approve) */}
              {needsTruth && !isApproved && !isRejected && (
                <div
                  className="border border-amber-200/70 dark:border-amber-800/50 rounded-md p-3 bg-amber-50/40 dark:bg-amber-950/20 space-y-1.5"
                  data-testid={`cv-suggestion-truth-${idx}`}
                >
                  <p className="text-[11px] uppercase tracking-wider font-semibold text-amber-700 dark:text-amber-300 flex items-center gap-1.5">
                    <AlertTriangleIcon className="w-3 h-3" />
                    Confirm before adding
                  </p>
                  {TRUTH_CHECKS.map((c) => (
                    <label key={c.key} className="flex items-start gap-2 text-xs text-foreground cursor-pointer">
                      <input
                        type="checkbox"
                        className="mt-0.5"
                        checked={!!(truthState[key]?.[c.key])}
                        onChange={() => toggleTruth(key, c.key)}
                        data-testid={`cv-suggestion-truth-${idx}-${c.key}`}
                      />
                      <span>{c.label}</span>
                    </label>
                  ))}
                  {!truthOk && (
                    <p className="text-[11px] text-amber-700 dark:text-amber-300 mt-1">
                      Tick all three boxes to approve this suggestion.
                    </p>
                  )}
                </div>
              )}

              {/* Edit panel */}
              {isEditing && (
                <div className="space-y-2" data-testid={`cv-suggestion-edit-${idx}`}>
                  <Textarea
                    value={draftText}
                    onChange={(e) => setDraftText(e.target.value)}
                    rows={3}
                    className="text-sm"
                    placeholder="Rewrite the line as it should appear in your CV..."
                    data-testid={`cv-suggestion-edit-input-${idx}`}
                  />
                </div>
              )}

              {/* Actions */}
              <div className="flex items-center gap-2 flex-wrap">
                {!isApproved && !isRejected && !isEditing && (
                  <>
                    {insertableText && <Button
                      size="sm"
                      variant="outline"
                      className="h-8 text-xs"
                      onClick={() => handleApprove(key, s, idx)}
                      disabled={disabled || (needsTruth && !truthOk)}
                      data-testid={`cv-suggestion-approve-btn-${idx}`}
                    >
                      <CheckIcon className="w-3 h-3 mr-1.5" /> Approve &amp; Add
                    </Button>}
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-8 text-xs"
                      onClick={() => handleStartEdit(key, s)}
                      disabled={disabled}
                      data-testid={`cv-suggestion-edit-btn-${idx}`}
                    >
                      <Edit2Icon className="w-3 h-3 mr-1.5" /> Edit
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-8 text-xs text-muted-foreground"
                      onClick={() => handleReject(key)}
                      disabled={disabled}
                      data-testid={`cv-suggestion-reject-btn-${idx}`}
                    >
                      <XIcon className="w-3 h-3 mr-1.5" /> Reject
                    </Button>
                  </>
                )}
                {isEditing && (
                  <>
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-8 text-xs"
                      onClick={() => handleSaveEdit(key, s)}
                      disabled={disabled || !isSafeSuggestionEditText(draftText) || (needsTruth && !truthOk)}
                      data-testid={`cv-suggestion-save-edit-btn-${idx}`}
                    >
                      <CheckIcon className="w-3 h-3 mr-1.5" /> Approve &amp; Add edited
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-8 text-xs text-muted-foreground"
                      onClick={() => { setEditingKey(null); setDraftText(''); }}
                      disabled={disabled}
                      data-testid={`cv-suggestion-cancel-edit-btn-${idx}`}
                    >
                      Cancel
                    </Button>
                  </>
                )}
                {(isApproved || isRejected) && (
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-8 text-xs text-muted-foreground"
                    onClick={() => setDecision(key, { status: 'pending', edited_text: null, applied_section: null, truth_acknowledged: false })}
                    disabled={disabled}
                    data-testid={`cv-suggestion-undo-btn-${idx}`}
                  >
                    Undo decision
                  </Button>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// ── Main Editor Page ──────────────────────────────────────────────────

export default function EditorPage() {
  const { jobResultId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [cvModel, setCvModel] = useState(null);
  const [coverLetterText, setCoverLetterText] = useState('');
  const [savingCv, setSavingCv] = useState(false);
  const [savingCl, setSavingCl] = useState(false);

  // Save status: 'idle' | 'saved' | 'error'  (transient 'saving' handled via savingCv/savingCl)
  const [cvSaveStatus, setCvSaveStatus] = useState('idle');
  const [clSaveStatus, setClSaveStatus] = useState('idle');
  const [cvSaveErrMsg, setCvSaveErrMsg] = useState('');
  const [clSaveErrMsg, setClSaveErrMsg] = useState('');
  const [cvSavedAt, setCvSavedAt] = useState(null);
  const [clSavedAt, setClSavedAt] = useState(null);

  // Snapshots of the last-saved content for dirty tracking
  const [savedCvSnapshot, setSavedCvSnapshot] = useState(''); // JSON string of denormalized CV
  const [savedClSnapshot, setSavedClSnapshot] = useState(''); // plain cover letter text

  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [downloadingDocx, setDownloadingDocx] = useState(false);
  const [downloadError, setDownloadError] = useState('');

  // Review-before-download modal
  const [reviewOpen, setReviewOpen] = useState(false);
  const [reviewFormat, setReviewFormat] = useState('pdf'); // 'pdf' | 'docx'

  // Autosave debounce timers
  const cvAutosaveTimer = useRef(null);
  const clAutosaveTimer = useRef(null);

  // Load editor data
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const d = await fetchEditorData(jobResultId);
        if (cancelled) return;
        setData(d);
        // Precedence: meaningful cv_user_edits_json -> tailored_cv_json -> noCv state.
        // {} / null / undefined / object with only empty fields is NOT meaningful.
        let baseCv;
        if (hasMeaningfulCvContent(d.cv_user_edits_json)) {
          baseCv = d.cv_user_edits_json;
        } else if (hasMeaningfulCvContent(d.tailored_cv_json)) {
          baseCv = d.tailored_cv_json;
        } else {
          baseCv = null;
        }
        if (baseCv) {
          const normalized = normalizeCv(baseCv);
          setCvModel(normalized);
          // Snapshot = serialized denormalized CV at load time (matches what would be POSTed)
          setSavedCvSnapshot(JSON.stringify(denormalizeCv(normalized)));
        } else {
          // Explicit no-CV state — render an error rather than a blank editor.
          setCvModel(null);
          setSavedCvSnapshot('');
        }
        const clText = d.cover_letter_user_edits || d.cover_letter || '';
        setCoverLetterText(clText);
        setSavedClSnapshot(clText);
      } catch (e) {
        if (!cancelled) setLoadError(humanizeApiError(e, { context: 'editor-load' }));
      }
    })();
    return () => { cancelled = true; };
  }, [jobResultId]);

  // Derived dirty flags
  const cvDirty = useMemo(() => {
    if (!cvModel) return false;
    try { return JSON.stringify(denormalizeCv(cvModel)) !== savedCvSnapshot; } catch { return false; }
  }, [cvModel, savedCvSnapshot]);
  const clDirty = coverLetterText !== savedClSnapshot;

  // Section-level "AI tailored" badges, derived from cv_changes_summary returned
  // by /api/job-results/{id}/cv-status. We do NOT diff user edits.
  const [cvAiMeta, setCvAiMeta] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const s = await fetchTailoredCvStatus(jobResultId);
        if (!cancelled) setCvAiMeta(s);
      } catch (_) {
        if (!cancelled) setCvAiMeta(null);
      }
    })();
    return () => { cancelled = true; };
  }, [jobResultId]);

  const aiChangesSummary = (cvAiMeta?.cv_changes_summary || '').trim();
  const aiUnsupported = Array.isArray(cvAiMeta?.cv_unsupported_items_avoided)
    ? cvAiMeta.cv_unsupported_items_avoided
    : [];
  const aiChangedSections = useMemo(
    () => detectAiChangedSections(aiChangesSummary),
    [aiChangesSummary]
  );
  const hasAiChangesPanel = !!aiChangesSummary || aiUnsupported.length > 0;

  // ── CV improvement suggestions + per-suggestion decisions ────────────
  // We load the suggestion array via a Supabase whitelisted SELECT
  // (fetchSingleJobResult). Decisions ledger is sourced from /editor-data so
  // both come through backend-respected paths.
  const [cvSuggestions, setCvSuggestions] = useState([]);
  const [cvSuggestionDecisions, setCvSuggestionDecisions] = useState({});
  const [decisionsSaveStatus, setDecisionsSaveStatus] = useState('idle'); // idle | saving | saved | error
  const [decisionsSaveError, setDecisionsSaveError] = useState('');
  const decisionsAutosaveTimer = useRef(null);
  const decisionsLoadedRef = useRef(false);
  const decisionsSnapshotRef = useRef('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const row = await fetchSingleJobResult(jobResultId);
        if (cancelled) return;
        const sugg = Array.isArray(row?.cv_improvement_suggestions)
          ? row.cv_improvement_suggestions
          : [];
        setCvSuggestions(sugg.map(normalizeSuggestionForEditor).filter((item) => item.display_text || item.insertable_text));
      } catch (_) {
        if (!cancelled) setCvSuggestions([]);
      }
    })();
    return () => { cancelled = true; };
  }, [jobResultId]);

  // Hydrate decisions from editor-data once it loads.
  useEffect(() => {
    if (data && !decisionsLoadedRef.current) {
      const d = data.cv_suggestion_decisions_json;
      const initial = (d && typeof d === 'object' && !Array.isArray(d)) ? d : {};
      // Seed the snapshot BEFORE the state setter so the save effect treats
      // hydration as a no-op (no PATCH on mount).
      decisionsSnapshotRef.current = JSON.stringify(initial);
      setCvSuggestionDecisions(initial);
      decisionsLoadedRef.current = true;
    }
  }, [data]);

  // Debounced PATCH /cv-suggestion-decisions whenever decisions change. NEVER
  // fires on mount — gated by decisionsLoadedRef AND by the snapshot ref
  // seeded during hydration so the initial render is a no-op.
  useEffect(() => {
    if (!decisionsLoadedRef.current) return undefined;
    const serialised = JSON.stringify(cvSuggestionDecisions || {});
    if (serialised === decisionsSnapshotRef.current) return undefined;
    if (decisionsAutosaveTimer.current) clearTimeout(decisionsAutosaveTimer.current);
    decisionsAutosaveTimer.current = setTimeout(async () => {
      setDecisionsSaveStatus('saving');
      setDecisionsSaveError('');
      try {
        await saveCvSuggestionDecisions(jobResultId, cvSuggestionDecisions || {});
        decisionsSnapshotRef.current = serialised;
        setDecisionsSaveStatus('saved');
      } catch (e) {
        setDecisionsSaveStatus('error');
        setDecisionsSaveError(humanizeApiError(e, { context: 'cv-suggestion-decisions' }));
      }
    }, 1200);
    return () => { if (decisionsAutosaveTimer.current) clearTimeout(decisionsAutosaveTimer.current); };
  }, [cvSuggestionDecisions, jobResultId]);

  // Map a suggestion section name onto the editor model.
  const applySuggestionToCvModel = useCallback((suggestionKeyStr, text, sectionRaw) => {
    if (!text || !text.trim()) return;
    const section = String(sectionRaw || '').toLowerCase();
    setCvModel((m) => {
      if (!m) return m;
      // Routing rules — conservative; if we cannot confidently match a
      // section we append the line as the FIRST bullet of the most-recent
      // experience (or summary if no experience exists), then surface the
      // section the line landed in via the decisions ledger.
      if (section.includes('summary') || section.includes('objective') || section.includes('profile')) {
        const sep = m.summary && m.summary.trim() ? ' ' : '';
        return { ...m, summary: `${m.summary || ''}${sep}${text}`.trim() };
      }
      if (section.includes('skill')) {
        const groups = [...(m.skillGroups || [])];
        const target = groups[0] || { category: 'Skills', items: [] };
        const items = Array.isArray(target.items) ? [...target.items] : [];
        if (!items.some((i) => i.trim().toLowerCase() === text.trim().toLowerCase())) {
          items.push(text);
        }
        groups[0] = { ...target, items };
        return { ...m, skillGroups: groups };
      }
      if (section.includes('project')) {
        const projs = [...(m.projects || [])];
        if (projs.length === 0) {
          projs.push({ name: 'Project', description: '', bullets: [text] });
        } else {
          const last = { ...projs[projs.length - 1] };
          last.bullets = Array.isArray(last.bullets) ? [...last.bullets, text] : [text];
          projs[projs.length - 1] = last;
        }
        return { ...m, projects: projs };
      }
      if (section.includes('education')) {
        const edu = [...(m.education || [])];
        if (edu.length === 0) {
          edu.push({ degree: text, institution: '', location: '', year: '' });
        } else {
          const last = { ...edu[edu.length - 1] };
          last.degree = last.degree ? `${last.degree} — ${text}` : text;
          edu[edu.length - 1] = last;
        }
        return { ...m, education: edu };
      }
      // Default → append as bullet to most-recent experience entry.
      const exp = [...(m.experience || [])];
      if (exp.length === 0) {
        // No experience yet — fall back to summary so user never loses the line.
        const sep = m.summary && m.summary.trim() ? ' ' : '';
        return { ...m, summary: `${m.summary || ''}${sep}${text}`.trim() };
      }
      const last = { ...exp[exp.length - 1] };
      last.bullets = Array.isArray(last.bullets) ? [...last.bullets, text] : [text];
      exp[exp.length - 1] = last;
      return { ...m, experience: exp };
    });
    // Dirty flag flips → existing autosave debounce will PATCH /cv-edits.
  }, []);

  // ── Mutators ─────────────────────────────────────

  const markCvEditing = () => {
    if (cvSaveStatus === 'saved' || cvSaveStatus === 'no-changes') setCvSaveStatus('idle');
  };
  const markLetterEditing = () => {
    if (clSaveStatus === 'saved') setClSaveStatus('idle');
  };

  const updateHeader = (k, v) => { markCvEditing(); setCvModel((m) => ({ ...m, header: { ...m.header, [k]: v } })); };
  const updateSummary = (v) => { markCvEditing(); setCvModel((m) => ({ ...m, summary: v })); };

  // Skill groups
  const updateSkillGroup = (i, patch) => setCvModel((m) => {
    const groups = [...m.skillGroups];
    groups[i] = { ...groups[i], ...patch };
    return { ...m, skillGroups: groups };
  });
  const addSkillGroup = () => setCvModel((m) => ({ ...m, skillGroups: [...m.skillGroups, { category: '', items: [] }] }));
  const removeSkillGroup = (i) => setCvModel((m) => ({ ...m, skillGroups: m.skillGroups.filter((_, idx) => idx !== i) }));

  // Experience
  const updateExp = (i, patch) => setCvModel((m) => {
    const exp = [...m.experience];
    exp[i] = { ...exp[i], ...patch };
    return { ...m, experience: exp };
  });
  const addExp = () => setCvModel((m) => ({ ...m, experience: [...m.experience, { title: '', company: '', location: '', dates: '', bullets: [], description: '' }] }));
  const removeExp = (i) => setCvModel((m) => ({ ...m, experience: m.experience.filter((_, idx) => idx !== i) }));

  // Projects
  const updateProj = (i, patch) => setCvModel((m) => {
    const list = [...m.projects];
    list[i] = { ...list[i], ...patch };
    return { ...m, projects: list };
  });
  const addProj = () => setCvModel((m) => ({ ...m, projects: [...m.projects, { name: '', description: '', bullets: [] }] }));
  const removeProj = (i) => setCvModel((m) => ({ ...m, projects: m.projects.filter((_, idx) => idx !== i) }));

  // Education
  const updateEdu = (i, patch) => setCvModel((m) => {
    const list = [...m.education];
    list[i] = { ...list[i], ...patch };
    return { ...m, education: list };
  });
  const addEdu = () => setCvModel((m) => ({ ...m, education: [...m.education, { degree: '', institution: '', location: '', year: '' }] }));
  const removeEdu = (i) => setCvModel((m) => ({ ...m, education: m.education.filter((_, idx) => idx !== i) }));

  // Languages
  const updateLang = (i, patch) => setCvModel((m) => {
    const list = [...m.languages];
    list[i] = { ...list[i], ...patch };
    return { ...m, languages: list };
  });
  const addLang = () => setCvModel((m) => ({ ...m, languages: [...m.languages, { language: '', level: '' }] }));
  const removeLang = (i) => setCvModel((m) => ({ ...m, languages: m.languages.filter((_, idx) => idx !== i) }));

  // ── Save handlers ────────────────────────────────

  const handleSaveCv = useCallback(async () => {
    if (!cvModel) return;
    // No-op guard: never PATCH /cv-edits when nothing has actually changed.
    // Backend now hard-rejects empty {} too; this gives the user a clear message.
    if (!cvDirty) {
      setCvSaveStatus('no-changes');
      setCvSaveErrMsg('');
      setTimeout(() => {
        setCvSaveStatus((s) => (s === 'no-changes' ? 'idle' : s));
      }, 2500);
      return;
    }
    setSavingCv(true);
    setCvSaveErrMsg('');
    setCvSaveStatus('idle');
    try {
      const json = denormalizeCv(cvModel);
      await saveCvEdits(jobResultId, json);
      setSavedCvSnapshot(JSON.stringify(json));
      setCvSaveStatus('saved');
      setCvSavedAt(Date.now());
    } catch (e) {
      setCvSaveStatus('error');
      setCvSaveErrMsg(humanizeApiError(e, { context: 'editor-save' }));
    } finally {
      setSavingCv(false);
    }
  }, [cvModel, cvDirty, jobResultId]);

  // ── Autosave (debounce 2s, also fires on blur) ──────────────────
  const scheduleAutosaveCv = useCallback(() => {
    if (cvAutosaveTimer.current) clearTimeout(cvAutosaveTimer.current);
    cvAutosaveTimer.current = setTimeout(() => { handleSaveCv(); }, 2000);
  }, [handleSaveCv]);

  useEffect(() => {
    if (!cvModel) return undefined;
    if (cvDirty) scheduleAutosaveCv();
    return () => { if (cvAutosaveTimer.current) clearTimeout(cvAutosaveTimer.current); };
  }, [cvModel, cvDirty, scheduleAutosaveCv]);

  const handleSaveCoverLetter = useCallback(async () => {
    setSavingCl(true);
    setClSaveErrMsg('');
    setClSaveStatus('idle');
    try {
      await saveCoverLetterEdits(jobResultId, coverLetterText);
      setSavedClSnapshot(coverLetterText);
      setClSaveStatus('saved');
      setClSavedAt(Date.now());
    } catch (e) {
      setClSaveStatus('error');
      setClSaveErrMsg(humanizeApiError(e, { context: 'editor-save' }));
    } finally {
      setSavingCl(false);
    }
  }, [coverLetterText, jobResultId]);

  const scheduleAutosaveCl = useCallback(() => {
    if (clAutosaveTimer.current) clearTimeout(clAutosaveTimer.current);
    clAutosaveTimer.current = setTimeout(() => { handleSaveCoverLetter(); }, 2000);
  }, [handleSaveCoverLetter]);

  useEffect(() => {
    if (clDirty) scheduleAutosaveCl();
    return () => { if (clAutosaveTimer.current) clearTimeout(clAutosaveTimer.current); };
  }, [clDirty, coverLetterText, scheduleAutosaveCl]);

  // beforeunload guard while user has unsaved changes or a save in flight
  useEffect(() => {
    const handler = (e) => {
      if (savingCv || savingCl || cvDirty || clDirty) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [savingCv, savingCl, cvDirty, clDirty]);

  const handleSaveAll = async () => {
    if (cvDirty) await handleSaveCv();
    if (clDirty) await handleSaveCoverLetter();
  };

  const performDownload = useCallback(async (format) => {
    if (format === 'pdf') {
      setDownloadingPdf(true);
    } else {
      setDownloadingDocx(true);
    }
    setDownloadError('');
    try {
      // Save latest CV edits first so the export reflects what the user sees
      if (cvModel && cvDirty) {
        const json = denormalizeCv(cvModel);
        await saveCvEdits(jobResultId, json);
        setSavedCvSnapshot(JSON.stringify(json));
        setCvSaveStatus('saved');
      }
      if (format === 'pdf') {
        await downloadTailoredCvPdf(jobResultId);
      } else {
        await downloadTailoredCvDocx(jobResultId);
      }
      setReviewOpen(false);
    } catch (e) {
      setDownloadError(humanizeApiError(e, { context: 'cv-export' }));
    } finally {
      if (format === 'pdf') setDownloadingPdf(false);
      else setDownloadingDocx(false);
    }
  }, [cvModel, cvDirty, jobResultId]);

  const openReviewForFormat = (format) => {
    setReviewFormat(format);
    setDownloadError('');
    setReviewOpen(true);
  };

  const handleResetCv = () => {
    if (!data) return;
    const base = data.tailored_cv_json || {};
    const normalized = normalizeCv(base);
    setCvModel(normalized);
    // Note: dirty flag will be re-derived — NOT saved until user clicks Save
    setCvSaveStatus('idle');
    setCvSaveErrMsg('');
  };

  const handleResetCoverLetter = () => {
    if (!data) return;
    setCoverLetterText(data.cover_letter || '');
    setClSaveStatus('idle');
    setClSaveErrMsg('');
  };

  // ── Render ───────────────────────────────────────

  if (loadError) {
    return (
      <div className="min-h-screen py-12 md:py-24" data-testid="editor-error">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <Link to="/job-results" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-4">
            <ArrowLeft className="w-4 h-4" /> Job Results
          </Link>
          <div className="border border-red-200 dark:border-red-800 rounded-xl p-10 text-center bg-red-50/30 dark:bg-red-950/20">
            <AlertCircle className="w-8 h-8 text-red-500 mx-auto mb-3" />
            <h2 className="text-lg font-medium mb-1">Unable to load editor</h2>
            <p className="text-sm text-muted-foreground">{toSafeDisplayString(loadError, { context: 'editor-load' })}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen py-12 md:py-24" data-testid="editor-loading">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <div className="h-6 w-40 bg-border/30 rounded animate-pulse mb-4" />
          <div className="h-10 w-64 bg-border/30 rounded animate-pulse mb-6" />
          <div className="space-y-3">{[1,2,3,4].map((i) => <div key={i} className="h-32 bg-border/20 rounded-lg animate-pulse" />)}</div>
        </div>
      </div>
    );
  }

  const cvReady = data.cv_status === 'completed' && !!data.tailored_cv_json && !!cvModel;
  const clReady = data.cover_letter_status === 'completed' && data.cover_letter;

  return (
    <div className="min-h-screen py-12 md:py-20" data-testid="editor-page">
      <div className="max-w-5xl mx-auto px-6 md:px-12">

        {/* ── Top bar ───────────────────────────────── */}
        <div className="mb-6">
          <button onClick={() => navigate('/job-results')} className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-4" data-testid="editor-back-btn">
            <ArrowLeft className="w-4 h-4" /> Back to results
          </button>
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <h1 className="font-serif-display text-3xl md:text-4xl tracking-tight font-medium">Edit CV &amp; Cover Letter</h1>
              <p className="text-sm text-muted-foreground mt-1.5">
                <span className="font-medium text-foreground">{data.job_title}</span>
                {data.company && <> · {data.company}</>}
                {data.location && <> · {data.location}</>}
                {data.fit && (
                  <span className="ml-2 text-[11px] px-2 py-0.5 border border-border rounded-full">
                    {data.fit} · {Number(data.total_score).toFixed(1)}
                  </span>
                )}
                {data.job_url && (
                  <a href={data.job_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 ml-3 text-xs hover:underline">
                    View listing <ExternalLink className="w-3 h-3" />
                  </a>
                )}
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-3 text-xs">
                <span className="text-muted-foreground">CV:</span>
                <SaveStatusBadge saving={savingCv} dirty={cvDirty} status={cvSaveStatus} errorMsg={cvSaveErrMsg} savedAt={cvSavedAt} testid="cv-save-status" />
                {!savingCv && cvSaveStatus === 'idle' && !cvDirty && <span className="text-muted-foreground/70">—</span>}
                <span className="text-muted-foreground ml-2">Letter:</span>
                <SaveStatusBadge saving={savingCl} dirty={clDirty} status={clSaveStatus} errorMsg={clSaveErrMsg} savedAt={clSavedAt} testid="cl-save-status" />
                {!savingCl && clSaveStatus === 'idle' && !clDirty && <span className="text-muted-foreground/70">—</span>}
              </div>
              <Button
                onClick={handleSaveAll}
                disabled={savingCv || savingCl || (!cvDirty && !clDirty)}
                className="btn-primary text-sm px-4 py-2 h-auto disabled:opacity-50"
                data-testid="editor-save-all-btn"
              >
                {(savingCv || savingCl) ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Save className="w-3.5 h-3.5 mr-1.5" />}
                {(savingCv || savingCl) ? 'Saving…' : 'Save all'}
              </Button>
            </div>
          </div>
          {(cvSaveErrMsg || clSaveErrMsg) && (
            <p className="text-xs text-red-500 mt-2" data-testid="editor-save-error">
              {toSafeDisplayString(cvSaveErrMsg || clSaveErrMsg, { context: 'editor-save' })}
            </p>
          )}
        </div>

        {/* ── CV Editor ─────────────────────────────── */}
        <section className="border border-border rounded-xl overflow-hidden mb-8" data-testid="cv-editor-section">
          <div className="p-4 bg-surface/40 border-b border-border flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <FileText className="w-4 h-4 text-muted-foreground" />
              <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Tailored CV</h2>
              <SaveStatusBadge saving={savingCv} dirty={cvDirty} status={cvSaveStatus} errorMsg={cvSaveErrMsg} testid="cv-section-status" />
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {cvReady && (
                <button onClick={handleResetCv} disabled={savingCv} className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground border border-border px-2.5 py-1.5 rounded-md hover:bg-surface transition-colors disabled:opacity-50" data-testid="cv-reset-btn">
                  <RotateCcw className="w-3 h-3" /> Reset
                </button>
              )}
              <button onClick={() => openReviewForFormat('pdf')} disabled={downloadingPdf || !cvReady || savingCv} className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground border border-border px-2.5 py-1.5 rounded-md hover:bg-surface transition-colors disabled:opacity-50" data-testid="editor-download-pdf-btn">
                {downloadingPdf ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />}
                {downloadingPdf ? 'Preparing…' : 'Download PDF'}
              </button>
              <button onClick={() => openReviewForFormat('docx')} disabled={downloadingDocx || !cvReady || savingCv} className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground border border-border px-2.5 py-1.5 rounded-md hover:bg-surface transition-colors disabled:opacity-50" data-testid="editor-download-docx-btn">
                {downloadingDocx ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />}
                {downloadingDocx ? 'Preparing…' : 'Download DOCX'}
              </button>
              <Button
                onClick={handleSaveCv}
                disabled={savingCv || !cvReady}
                className="btn-secondary text-xs px-3 py-1.5 h-auto disabled:opacity-50"
                data-testid="cv-save-btn"
              >
                {savingCv ? <Loader2 className="w-3 h-3 animate-spin mr-1.5" /> : <Save className="w-3 h-3 mr-1.5" />}
                {savingCv ? 'Saving…' : 'Save CV'}
              </Button>
            </div>
          </div>
          {downloadError && <p className="text-xs text-red-500 px-4 pt-3" data-testid="editor-download-error">{toSafeDisplayString(downloadError, { context: 'cv-export' })}</p>}

          {!cvReady ? (
            <div className="p-8 text-center text-sm text-muted-foreground" data-testid="cv-editor-unavailable">
              Your tailored CV isn&apos;t ready yet. Generate it from the job results page, then come back to edit.
            </div>
          ) : (
            <div className="p-5 md:p-6 space-y-8">
              {hasAiChangesPanel && (
                <div
                  className="rounded-md border border-amber-200/70 dark:border-amber-800/50 bg-amber-50/40 dark:bg-amber-950/20 p-3 text-xs space-y-2"
                  data-testid="ai-changes-panel"
                >
                  <div className="flex items-center gap-2 text-amber-700 dark:text-amber-300">
                    <Sparkles className="w-3.5 h-3.5" />
                    <span className="font-semibold uppercase tracking-wider">AI changes made</span>
                  </div>
                  <p className="text-xs text-muted-foreground italic">
                    Highlighted areas show AI-tailored changes.
                  </p>
                  {aiChangesSummary && (
                    <p className="text-sm leading-relaxed text-foreground" data-testid="ai-changes-summary">
                      {aiChangesSummary}
                    </p>
                  )}
                  {aiUnsupported.length > 0 && (
                    <div data-testid="ai-unsupported-items">
                      <span className="text-xs font-medium text-muted-foreground">Unsupported items avoided:</span>
                      <ul className="mt-1 space-y-0.5">
                        {aiUnsupported.map((u, i) => (
                          <li key={i} className="text-xs text-muted-foreground flex gap-1.5">
                            <AlertCircle className="w-3 h-3 shrink-0 mt-0.5 text-amber-500" />
                            {typeof u === 'string' ? u : (u.item || JSON.stringify(u))}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}

              {/* CV Suggestion Decisions — approve / edit / reject AI suggestions.
                  Approving inserts into cvModel which flows through the existing
                  cv-edits autosave path. Rejecting only writes the decisions
                  ledger via /cv-suggestion-decisions. */}
              {cvSuggestions.length > 0 && (
                <CvSuggestionsPanel
                  suggestions={cvSuggestions}
                  decisions={cvSuggestionDecisions}
                  onDecisionsChange={setCvSuggestionDecisions}
                  onApply={applySuggestionToCvModel}
                  disabled={savingCv || decisionsSaveStatus === 'saving'}
                />
              )}
              {cvSuggestions.length > 0 && decisionsSaveStatus === 'error' && (
                <p className="text-xs text-red-500 -mt-3" data-testid="cv-suggestions-save-error">
                  Could not save decisions: {toSafeDisplayString(decisionsSaveError || 'unknown error', { context: 'editor-save' })}
                </p>
              )}

              {/* Header */}
              <div>
                <SectionHeader title="Contact" />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <Input value={cvModel.header.name} onChange={(e) => updateHeader('name', e.target.value)} placeholder="Full name" className="text-sm" data-testid="cv-header-name" />
                  <Input value={cvModel.header.email} onChange={(e) => updateHeader('email', e.target.value)} placeholder="Email" className="text-sm" data-testid="cv-header-email" />
                  <Input value={cvModel.header.phone} onChange={(e) => updateHeader('phone', e.target.value)} placeholder="Phone" className="text-sm" data-testid="cv-header-phone" />
                  <Input value={cvModel.header.location} onChange={(e) => updateHeader('location', e.target.value)} placeholder="Location" className="text-sm" data-testid="cv-header-location" />
                  <Input value={cvModel.header.linkedin} onChange={(e) => updateHeader('linkedin', e.target.value)} placeholder="LinkedIn URL" className="text-sm" data-testid="cv-header-linkedin" />
                  <Input value={cvModel.header.github} onChange={(e) => updateHeader('github', e.target.value)} placeholder="GitHub URL" className="text-sm" data-testid="cv-header-github" />
                </div>
              </div>

              {/* Summary */}
              <div>
                <SectionHeader
                  title="Professional Summary"
                  badge={aiChangedSections?.summary ? <AiTailoredBadge testid="ai-badge-summary" /> : null}
                />
                <textarea
                  value={cvModel.summary}
                  onChange={(e) => updateSummary(e.target.value)}
                  rows={4}
                  className={fieldCls + ' resize-y'}
                  placeholder="A short summary about you..."
                  data-testid="cv-summary-input"
                />
              </div>

              {/* Skills */}
              <div>
                <SectionHeader
                  title="Skills"
                  badge={aiChangedSections?.skills ? <AiTailoredBadge testid="ai-badge-skills" /> : null}
                >
                  <button onClick={addSkillGroup} className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground" data-testid="cv-skill-group-add">
                    <Plus className="w-3 h-3" /> Add group
                  </button>
                </SectionHeader>
                <div className="space-y-4">
                  {cvModel.skillGroups.map((g, i) => (
                    <div key={i} className="p-3 border border-border rounded-lg" data-testid={`cv-skill-group-${i}`}>
                      <div className="flex items-center gap-2 mb-2">
                        <Input
                          value={g.category}
                          onChange={(e) => updateSkillGroup(i, { category: e.target.value })}
                          placeholder="Category (e.g. Languages, Frameworks)"
                          className="text-xs h-8 flex-1"
                          data-testid={`cv-skill-group-category-${i}`}
                        />
                        <button onClick={() => removeSkillGroup(i)} className="text-muted-foreground hover:text-red-500" aria-label="Remove group" data-testid={`cv-skill-group-remove-${i}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                      <textarea
                        value={g.items.join(', ')}
                        onChange={(e) => updateSkillGroup(i, { items: e.target.value.split(',').map((s) => s.trim()).filter(() => true) })}
                        rows={2}
                        placeholder="Comma-separated skills"
                        className={fieldCls + ' text-xs resize-y'}
                        data-testid={`cv-skill-group-items-${i}`}
                      />
                    </div>
                  ))}
                  {cvModel.skillGroups.length === 0 && <p className="text-xs text-muted-foreground">No skills yet. Click &quot;Add group&quot; to create one.</p>}
                </div>
              </div>

              {/* Experience */}
              <div>
                <SectionHeader
                  icon={Briefcase}
                  title="Experience"
                  badge={aiChangedSections?.experience ? <AiTailoredBadge testid="ai-badge-experience" /> : null}
                >
                  <button onClick={addExp} className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground" data-testid="cv-exp-add">
                    <Plus className="w-3 h-3" /> Add role
                  </button>
                </SectionHeader>
                <div className="space-y-4">
                  {cvModel.experience.map((e, i) => (
                    <div key={i} className="p-4 border border-border rounded-lg space-y-2" data-testid={`cv-exp-${i}`}>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        <Input value={e.title} onChange={(ev) => updateExp(i, { title: ev.target.value })} placeholder="Title" className="text-sm" data-testid={`cv-exp-title-${i}`} />
                        <Input value={e.company} onChange={(ev) => updateExp(i, { company: ev.target.value })} placeholder="Company" className="text-sm" data-testid={`cv-exp-company-${i}`} />
                        <Input value={e.location} onChange={(ev) => updateExp(i, { location: ev.target.value })} placeholder="Location" className="text-sm" data-testid={`cv-exp-location-${i}`} />
                        <Input value={e.dates} onChange={(ev) => updateExp(i, { dates: ev.target.value })} placeholder="Dates (e.g. 2021 – Present)" className="text-sm" data-testid={`cv-exp-dates-${i}`} />
                      </div>
                      <BulletList bullets={e.bullets} onChange={(b) => updateExp(i, { bullets: b })} testidPrefix={`cv-exp-${i}`} />
                      <div className="flex justify-end">
                        <button onClick={() => removeExp(i)} className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-red-500" data-testid={`cv-exp-remove-${i}`}>
                          <Trash2 className="w-3 h-3" /> Remove role
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Projects */}
              <div>
                <SectionHeader
                  title="Projects"
                  badge={aiChangedSections?.projects ? <AiTailoredBadge testid="ai-badge-projects" /> : null}
                >
                  <button onClick={addProj} className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground" data-testid="cv-proj-add">
                    <Plus className="w-3 h-3" /> Add project
                  </button>
                </SectionHeader>
                <div className="space-y-4">
                  {cvModel.projects.map((p, i) => (
                    <div key={i} className="p-4 border border-border rounded-lg space-y-2" data-testid={`cv-proj-${i}`}>
                      <Input value={p.name} onChange={(ev) => updateProj(i, { name: ev.target.value })} placeholder="Project name" className="text-sm" data-testid={`cv-proj-name-${i}`} />
                      <textarea value={p.description} onChange={(ev) => updateProj(i, { description: ev.target.value })} rows={2} className={fieldCls + ' resize-y'} placeholder="Short description" data-testid={`cv-proj-desc-${i}`} />
                      <BulletList bullets={p.bullets} onChange={(b) => updateProj(i, { bullets: b })} testidPrefix={`cv-proj-${i}`} />
                      <div className="flex justify-end">
                        <button onClick={() => removeProj(i)} className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-red-500" data-testid={`cv-proj-remove-${i}`}>
                          <Trash2 className="w-3 h-3" /> Remove
                        </button>
                      </div>
                    </div>
                  ))}
                  {cvModel.projects.length === 0 && <p className="text-xs text-muted-foreground">No projects yet.</p>}
                </div>
              </div>

              {/* Education */}
              <div>
                <SectionHeader
                  title="Education"
                  badge={aiChangedSections?.education ? <AiTailoredBadge testid="ai-badge-education" /> : null}
                >
                  <button onClick={addEdu} className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground" data-testid="cv-edu-add">
                    <Plus className="w-3 h-3" /> Add education
                  </button>
                </SectionHeader>
                <div className="space-y-4">
                  {cvModel.education.map((e, i) => (
                    <div key={i} className="p-3 border border-border rounded-lg space-y-2" data-testid={`cv-edu-${i}`}>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        <Input value={e.degree} onChange={(ev) => updateEdu(i, { degree: ev.target.value })} placeholder="Degree / title" className="text-sm" data-testid={`cv-edu-degree-${i}`} />
                        <Input value={e.institution} onChange={(ev) => updateEdu(i, { institution: ev.target.value })} placeholder="Institution" className="text-sm" data-testid={`cv-edu-institution-${i}`} />
                        <Input value={e.location} onChange={(ev) => updateEdu(i, { location: ev.target.value })} placeholder="Location" className="text-sm" data-testid={`cv-edu-location-${i}`} />
                        <Input value={e.year} onChange={(ev) => updateEdu(i, { year: ev.target.value })} placeholder="Year" className="text-sm" data-testid={`cv-edu-year-${i}`} />
                      </div>
                      <div className="flex justify-end">
                        <button onClick={() => removeEdu(i)} className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-red-500" data-testid={`cv-edu-remove-${i}`}>
                          <Trash2 className="w-3 h-3" /> Remove
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Languages */}
              <div>
                <SectionHeader
                  title="Languages"
                  badge={aiChangedSections?.languages ? <AiTailoredBadge testid="ai-badge-languages" /> : null}
                >
                  <button onClick={addLang} className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground" data-testid="cv-lang-add">
                    <Plus className="w-3 h-3" /> Add language
                  </button>
                </SectionHeader>
                <div className="space-y-2">
                  {cvModel.languages.map((l, i) => (
                    <div key={i} className="flex items-center gap-2" data-testid={`cv-lang-${i}`}>
                      <Input value={l.language} onChange={(ev) => updateLang(i, { language: ev.target.value })} placeholder="Language" className="text-sm flex-1" data-testid={`cv-lang-name-${i}`} />
                      <Input value={l.level} onChange={(ev) => updateLang(i, { level: ev.target.value })} placeholder="Level (e.g. Native, C1)" className="text-sm flex-1" data-testid={`cv-lang-level-${i}`} />
                      <button onClick={() => removeLang(i)} className="text-muted-foreground hover:text-red-500" aria-label="Remove language" data-testid={`cv-lang-remove-${i}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </section>

        {/* ── Cover Letter Editor ───────────────────── */}
        <section className="border border-border rounded-xl overflow-hidden mb-10" data-testid="cover-letter-editor-section">
          <div className="p-4 bg-surface/40 border-b border-border flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <Mail className="w-4 h-4 text-muted-foreground" />
              <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Cover Letter</h2>
              <SaveStatusBadge saving={savingCl} dirty={clDirty} status={clSaveStatus} errorMsg={clSaveErrMsg} testid="cl-section-status" />
            </div>
            <div className="flex items-center gap-2">
              {clReady && (
                <button onClick={handleResetCoverLetter} disabled={savingCl} className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground border border-border px-2.5 py-1.5 rounded-md hover:bg-surface transition-colors disabled:opacity-50" data-testid="cl-reset-btn">
                  <RotateCcw className="w-3 h-3" /> Reset
                </button>
              )}
              <Button
                onClick={handleSaveCoverLetter}
                disabled={savingCl || !clReady || !clDirty}
                className="btn-secondary text-xs px-3 py-1.5 h-auto disabled:opacity-50"
                data-testid="cl-save-btn"
              >
                {savingCl ? <Loader2 className="w-3 h-3 animate-spin mr-1.5" /> : <Save className="w-3 h-3 mr-1.5" />}
                {savingCl ? 'Saving…' : 'Save letter'}
              </Button>
            </div>
          </div>
          {!clReady ? (
            <div className="p-8 text-center text-sm text-muted-foreground" data-testid="cl-editor-unavailable">
              Your cover letter isn&apos;t ready yet. Generate it from the job results page, then come back to edit.
            </div>
          ) : (
            <div className="p-5 md:p-6">
              {data.cover_letter_subject && (
                <div className="mb-3 text-xs text-muted-foreground">
                  <span className="font-medium text-foreground">Subject:</span> {data.cover_letter_subject}
                </div>
              )}
              <textarea
                value={coverLetterText}
                onChange={(e) => setCoverLetterText(e.target.value)}
                rows={18}
                className={fieldCls + ' resize-y text-sm leading-relaxed whitespace-pre-line min-h-[400px]'}
                placeholder="Your cover letter..."
                data-testid="cl-editor-textarea"
              />
              <p className="text-xs text-muted-foreground mt-2">{coverLetterText.split(/\s+/).filter(Boolean).length} words</p>
            </div>
          )}
        </section>

        {/* ── Review-before-download modal ─────────────── */}
        <ReviewBeforeDownloadModal
          open={reviewOpen}
          onClose={() => setReviewOpen(false)}
          onConfirm={() => performDownload(reviewFormat)}
          jobResultId={jobResultId}
          cvGeneratedAt={cvSavedAt ? `edited-${cvSavedAt}` : (data?.user_edits_updated_at || data?.cv_status || 'unknown')}
          format={reviewFormat}
          inProgress={reviewFormat === 'pdf' ? downloadingPdf : downloadingDocx}
        />

      </div>
    </div>
  );
}
